import {
  Alert,
  AlertDescription,
  AlertIcon,
  Center,
  Checkbox,
  Circle,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  InputGroup,
  InputRightElement,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useEffect, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { Assignment } from '@finley-technologies/finley-types';

import {
  LoanProductConfig,
  TotalAssignableExposureHeuristicConfig,
} from '../../types';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import {
  dollarNumberWithCommas,
  numberToPercentageLabel,
} from 'packages/common-frontend/utils';
import { GetCreditFacilitiesAndLoanProductsWithBorrowingBaseMechanicsForAssignmentGroupingQueryGQLType } from 'packages/credit-facility/requests/requests.gql.generated';
import {
  UnderlinedHeader,
  TextWrapper,
} from 'packages/loanProduct/ui/assignmentStrategy/assignmentStrategyForm/shared';

const isHardRequirement = (
  test: string,
): test is Assignment.HardRequirementType => {
  return ['none', 'minimum', 'maximum'].includes(test);
};

type CreditFacilitySliderProps = {
  creditFacilityId: string;
  creditFacilityName: string;
  totalAssignableExposure: number | undefined;
  currentLoanProductConfig: LoanProductConfig;
  setHeuristicConfig: React.Dispatch<
    React.SetStateAction<Assignment.DecisionHeuristicConfig | undefined>
  >;
  totalRatiosExceed1: boolean;
  readOnly: boolean;
};
const CreditFacilitySlider = ({
  creditFacilityId,
  creditFacilityName,
  totalAssignableExposure,
  currentLoanProductConfig,
  setHeuristicConfig,
  totalRatiosExceed1,
  readOnly,
}: CreditFacilitySliderProps) => {
  const creditFacilityConfig = currentLoanProductConfig.creditFacilityAllocations.find(
    ({ creditFacilityId: currCreditFacilityId }) =>
      currCreditFacilityId === creditFacilityId,
  );
  if (!creditFacilityConfig) {
    throw Error(
      `Could not find credit facility config for ${creditFacilityName} and in total assignable exposure heuristic config for loan product id ${currentLoanProductConfig.loanProductId}`,
    );
  }

  // convert from 0-1 => 0-100
  const { allocation, hardRequirement } = creditFacilityConfig;
  const numericAllocation = parseFloat((allocation * 100).toFixed(1));

  // this is the checkbox that will then open the hard min/max radio buttons
  const handleHardRequirementCheck = (checked: boolean) => {
    setHeuristicConfig((oldHeuristicConfig) => {
      if (!oldHeuristicConfig) return oldHeuristicConfig;

      finleyAssert(
        oldHeuristicConfig.heuristicType === 'assignByTotalAssignableExposure',
        'heuristicType must be assignByTotalAssignableExposure',
      );

      return {
        heuristicType: oldHeuristicConfig.heuristicType,
        config: {
          loanProductConfigs: oldHeuristicConfig.config.loanProductConfigs.map(
            (loanProductConfig) => {
              const updatedLoanProductConfig = { ...loanProductConfig };
              if (
                updatedLoanProductConfig.loanProductId ===
                currentLoanProductConfig.loanProductId
              ) {
                updatedLoanProductConfig.creditFacilityAllocations = updatedLoanProductConfig.creditFacilityAllocations.map(
                  (creditFacility) => {
                    const updatedCreditFacility = { ...creditFacility };
                    if (
                      updatedCreditFacility.creditFacilityId ===
                      creditFacilityId
                    ) {
                      if (checked && hardRequirement === 'none') {
                        updatedCreditFacility.hardRequirement = 'minimum';
                      } else {
                        updatedCreditFacility.hardRequirement = 'none';
                      }
                    } else {
                      // since only one facility can be a hard requirement at a time, reset others once this is checked
                      updatedCreditFacility.hardRequirement = 'none';
                    }
                    return updatedCreditFacility;
                  },
                );
              }
              return updatedLoanProductConfig;
            },
          ),
        },
      };
    });
  };

  // this is switching between the hard min/max radio buttons
  const handleHardRequirementChange = (value: string) => {
    if (!(isHardRequirement(value) && value !== 'none')) {
      throw Error(
        `Hard requirement expected to be either 'minimum' or 'maximum' but got ${value}`,
      );
    }
    setHeuristicConfig((oldHeuristicConfig) => {
      if (!oldHeuristicConfig) return oldHeuristicConfig;

      finleyAssert(
        oldHeuristicConfig.heuristicType === 'assignByTotalAssignableExposure',
        'heuristicType must be assignByTotalAssignableExposure',
      );

      return {
        heuristicType: oldHeuristicConfig.heuristicType,
        config: {
          loanProductConfigs: oldHeuristicConfig.config.loanProductConfigs.map(
            (loanProductConfig) => {
              const updatedLoanProductConfig = { ...loanProductConfig };
              if (
                updatedLoanProductConfig.loanProductId ===
                currentLoanProductConfig.loanProductId
              ) {
                updatedLoanProductConfig.creditFacilityAllocations = updatedLoanProductConfig.creditFacilityAllocations.map(
                  (creditFacility) => {
                    const updatedCreditFacility = { ...creditFacility };
                    if (
                      updatedCreditFacility.creditFacilityId ===
                      creditFacilityId
                    ) {
                      updatedCreditFacility.hardRequirement = value;
                    }
                    return updatedCreditFacility;
                  },
                );
              }
              return updatedLoanProductConfig;
            },
          ),
        },
      };
    });
  };

  // have this for the text input, to support partial decimal points
  // this will then get converted to a number for update to global state
  const [
    localNumericAllocation,
    setLocalNumericAllocation,
  ] = useState<string>();

  useEffect(() => {
    setLocalNumericAllocation(numericAllocation.toFixed(1));
  }, [numericAllocation]);

  // update allocation number from either input or slider
  const setAllocationChange = (updatedNumericAllocation: number) => {
    // convert back to 0 -> 1
    // make the balance sheet adjustment here
    const facilityRatioAllocation = parseFloat(
      (updatedNumericAllocation / 100).toFixed(3),
    );
    setLocalNumericAllocation(updatedNumericAllocation.toFixed(1));

    setHeuristicConfig((oldHeuristicConfig) => {
      if (!oldHeuristicConfig) return oldHeuristicConfig;

      finleyAssert(
        oldHeuristicConfig.heuristicType === 'assignByTotalAssignableExposure',
        'heuristicType must be assignByTotalAssignableExposure',
      );

      return {
        heuristicType: oldHeuristicConfig.heuristicType,
        config: {
          loanProductConfigs: oldHeuristicConfig.config.loanProductConfigs.map(
            (loanProductConfig) => {
              const updatedLoanProductConfig = { ...loanProductConfig };
              if (
                updatedLoanProductConfig.loanProductId ===
                currentLoanProductConfig.loanProductId
              ) {
                updatedLoanProductConfig.creditFacilityAllocations = updatedLoanProductConfig.creditFacilityAllocations.map(
                  (creditFacility) => {
                    const updatedCreditFacility = { ...creditFacility };
                    if (
                      updatedCreditFacility.creditFacilityId ===
                      creditFacilityId
                    ) {
                      updatedCreditFacility.allocation = facilityRatioAllocation;
                    }
                    return updatedCreditFacility;
                  },
                );
              }
              // update the balance sheet ratio to match the facility ratios
              const totalFacilityRatios = _.sumBy(
                updatedLoanProductConfig.creditFacilityAllocations,
                ({ allocation: currAllocation }) => currAllocation,
              );
              updatedLoanProductConfig.balanceSheetAllocation = _.round(
                1 - totalFacilityRatios,
                3,
              );
              return updatedLoanProductConfig;
            },
          ),
        },
      };
    });
  };

  return (
    <>
      <GridItem colSpan={3} pr="32px">
        <TextWrapper fontWeight="bold">{creditFacilityName}</TextWrapper>
      </GridItem>
      <GridItem colSpan={3} pr="32px">
        <VStack spacing={4} w="100%">
          <InputGroup>
            <NumberInput
              value={localNumericAllocation}
              precision={1}
              isInvalid={totalRatiosExceed1}
              // allow local change, only update global state upon click out
              onChange={(numString) => {
                setLocalNumericAllocation(numString);
              }}
              onBlur={(e) => {
                const updatedNumericAllocation = Number(e.target.value);
                setAllocationChange(updatedNumericAllocation);
              }}
              textAlign="right"
              isDisabled={readOnly}
            >
              <NumberInputField background="white" />
              <InputRightElement>
                <Icon icon="percent" color="grey" />
              </InputRightElement>
            </NumberInput>
          </InputGroup>
          <HStack spacing={4} alignSelf="baseline">
            <Checkbox
              isChecked={hardRequirement !== 'none'}
              onChange={(e) => {
                handleHardRequirementCheck(Boolean(e.target.checked));
              }}
              isDisabled={readOnly}
              background="white"
            />
            <Text opacity={readOnly ? 0.4 : 1}>Hard Requirement</Text>
          </HStack>
          {hardRequirement !== 'none' && (
            <RadioGroup
              onChange={(value: string) => handleHardRequirementChange(value)}
              value={hardRequirement}
              alignSelf="baseline"
              isDisabled={readOnly}
            >
              <Stack direction="row">
                <Radio value="minimum" background="white">
                  Minimum
                </Radio>
                <Radio value="maximum" background="white">
                  Maximum
                </Radio>
              </Stack>
            </RadioGroup>
          )}
        </VStack>
      </GridItem>
      <GridItem colSpan={4} pr="32px" alignSelf="center">
        <Slider
          value={numericAllocation}
          defaultValue={numericAllocation}
          min={0}
          max={100}
          step={0.5}
          onChange={setAllocationChange}
          w="100%"
          pt="48px"
          isDisabled={readOnly}
        >
          <SliderTrack>
            <SliderFilledTrack bgColor={colors.aurora[400]} />
          </SliderTrack>
          <SliderThumb>
            <Circle bgColor={colors.aurora[400]} size="16px" />
          </SliderThumb>
        </Slider>
      </GridItem>
      {totalAssignableExposure && (
        <GridItem colSpan={2}>
          <Text color="gray" size="14px" mb="8px">
            Projected Exposure
          </Text>
          <Text>
            {totalRatiosExceed1
              ? '-'
              : dollarNumberWithCommas(
                  creditFacilityConfig.allocation * totalAssignableExposure,
                )}
          </Text>
        </GridItem>
      )}
    </>
  );
};

export const DecisionHeuristicByTotalAssignableExposure = ({
  creditFacilitiesForAssignmentGroupingData,
  loanProductNameMap,
  totalAssignableExposureByLoanProductId,
  heuristicConfig,
  setHeuristicConfig,
  readOnly,
}: {
  creditFacilitiesForAssignmentGroupingData: GetCreditFacilitiesAndLoanProductsWithBorrowingBaseMechanicsForAssignmentGroupingQueryGQLType;
  loanProductNameMap: Map<string, string>;
  totalAssignableExposureByLoanProductId:
    | { [loanProductId: string]: number }
    | undefined;
  heuristicConfig: TotalAssignableExposureHeuristicConfig;
  setHeuristicConfig: React.Dispatch<
    React.SetStateAction<Assignment.DecisionHeuristicConfig | undefined>
  >;
  readOnly: boolean;
}) => {
  const [currentLoanProductId, setCurrentLoanProductId] = useState<string>();
  const [
    currentLoanProductConfig,
    setCurrentLoanProductConfig,
  ] = useState<LoanProductConfig>();

  useEffect(() => {
    if (heuristicConfig.loanProductConfigs.length > 0) {
      setCurrentLoanProductId(
        heuristicConfig.loanProductConfigs[0].loanProductId,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentLoanProductId) {
      setCurrentLoanProductConfig(
        heuristicConfig.loanProductConfigs.find(
          (config) => config.loanProductId === currentLoanProductId,
        ),
      );
    }
  }, [currentLoanProductId, heuristicConfig]);

  const loanProductOptions: {
    value: string;
    label: string;
  }[] = [];
  heuristicConfig.loanProductConfigs.forEach((config) => {
    const loanProductName = loanProductNameMap.get(config.loanProductId);
    if (loanProductName) {
      loanProductOptions.push({
        value: config.loanProductId,
        label: loanProductName,
      });
    }
  });
  let totalAssignableExposureForCurrentLoanProduct: number | undefined;
  if (totalAssignableExposureByLoanProductId && currentLoanProductId) {
    totalAssignableExposureForCurrentLoanProduct =
      totalAssignableExposureByLoanProductId[currentLoanProductId];
  }

  // show error state if allocations sum to > 1
  const checkAllocationsExceed1 = () => {
    if (currentLoanProductConfig) {
      const totalFacilityRatios = _.sumBy(
        currentLoanProductConfig.creditFacilityAllocations,
        ({ allocation: currAllocation }) => currAllocation,
      );
      return totalFacilityRatios > 1;
    }
    return false;
  };
  const totalRatiosExceed1 = checkAllocationsExceed1();
  return (
    <>
      {currentLoanProductId && currentLoanProductConfig && (
        <Flex mt="24px" mb="24px">
          <Flex direction="column" mr="24px" maxWidth="300px" minWidth="300px">
            <Text fontSize="md" mb="16px" fontWeight="600">
              Decision Heuristic by Total Assignment Amount
            </Text>
            <Text fontSize="md" mb="16px" color="gray">
              Total Assignment Exposure includes both newly originated and
              previously unassigned receivables.
            </Text>
          </Flex>
          <Flex
            direction="column"
            width="100%"
            border="1px"
            borderColor={colors.gray[200]}
            borderRadius="8px"
            padding="24px"
            background="#f9f6f1b3"
          >
            <Flex mb="32px">
              <Select
                value={currentLoanProductId}
                options={loanProductOptions}
                inputFloatingLabel="Loan Product"
                inputFloatingLabelBackgroundColor={colors.white}
                onChange={setCurrentLoanProductId}
                buttonProps={{ background: 'white' }}
              />
            </Flex>
            <UnderlinedHeader
              title="Total Assignable Exposure"
              endLabel={
                totalAssignableExposureForCurrentLoanProduct
                  ? dollarNumberWithCommas(
                      totalAssignableExposureForCurrentLoanProduct,
                    )
                  : undefined
              }
            />
            <Text fontSize="md" color="gray">
              The percentages entered must add up to 100% across all facilities
              and the balance sheet. You have the option to set a hard
              requirement for a single facility to ensure that Finley&apos;s
              proposed assignment stays within bounds. The only exception is if
              too many receivables have been excluded, preventing us from
              reaching the hard requirement. Any remaining exposure will be
              adjusted to meet the requirement.
            </Text>
            <Grid
              templateColumns="repeat(12, 1fr)"
              templateRows={`${'repeat(1, 96px) repeat(1, 8px)'.repeat(
                currentLoanProductConfig.creditFacilityAllocations.length - 1,
              )} repeat(1, 96px)`}
              m="24px 0"
              rowGap="48px"
            >
              {currentLoanProductConfig.creditFacilityAllocations.map(
                ({ creditFacilityId }) => {
                  const creditFacility = creditFacilitiesForAssignmentGroupingData.creditFacilitiesForAssignmentGrouping.find(
                    ({ id }) => id === creditFacilityId,
                  );

                  if (!creditFacility) {
                    throw Error(
                      `Credit facility id [${creditFacilityId}] in total assignable exposure heuristic config does not exist.`,
                    );
                  }

                  return (
                    <React.Fragment key={creditFacilityId}>
                      <CreditFacilitySlider
                        creditFacilityId={creditFacilityId}
                        currentLoanProductConfig={currentLoanProductConfig}
                        setHeuristicConfig={setHeuristicConfig}
                        creditFacilityName={creditFacility.name}
                        totalRatiosExceed1={totalRatiosExceed1}
                        totalAssignableExposure={
                          totalAssignableExposureForCurrentLoanProduct
                        }
                        readOnly={readOnly}
                      />
                      <GridItem colSpan={12}>
                        <Center h="100%">
                          <Divider />
                        </Center>
                      </GridItem>
                    </React.Fragment>
                  );
                },
              )}
            </Grid>
            {totalRatiosExceed1 && (
              <GridItem colSpan={12} mt="25px" mb="25px">
                <Alert status="error">
                  <AlertIcon />
                  <AlertDescription>
                    The cumulative ratios of total assignable exposure across
                    your facilities cannot exceed 100%.
                  </AlertDescription>
                </Alert>
              </GridItem>
            )}

            <UnderlinedHeader title="Left on Balance Sheet" />
            <Text fontSize="md" mb="24px" color="gray">
              Unassigned Exposure to be left on Balance Sheet.
            </Text>
            <Grid templateColumns="repeat(12, 1fr)">
              <GridItem colSpan={3}>
                <TextWrapper fontWeight="bold">Balance Sheet</TextWrapper>
              </GridItem>
              <GridItem colSpan={2}>
                <Text color="gray" size="14px" mb="8px">
                  Percent
                </Text>
                <Text>
                  {currentLoanProductConfig.balanceSheetAllocation >= 0
                    ? `${numberToPercentageLabel(
                        currentLoanProductConfig.balanceSheetAllocation,
                        2,
                      )}`
                    : '0%'}
                </Text>
              </GridItem>
              {totalAssignableExposureForCurrentLoanProduct && (
                <GridItem colSpan={2}>
                  <Text color="gray" size="14px" mb="8px">
                    Projected Exposure
                  </Text>
                  <Text>
                    {currentLoanProductConfig.balanceSheetAllocation >= 0
                      ? `${dollarNumberWithCommas(
                          currentLoanProductConfig.balanceSheetAllocation *
                            totalAssignableExposureForCurrentLoanProduct,
                        )}`
                      : '$0'}
                  </Text>
                </GridItem>
              )}
            </Grid>
          </Flex>
        </Flex>
      )}
      <hr />
    </>
  );
};
